<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');

const isSubmitting = ref(false);
const enabled = ref(false);
const inboxId = ref(null);

const apiInboxes = computed(() =>
  (inboxes.value || [])
    .filter(inbox => inbox.channel_type === 'Channel::Api')
    .sort((left, right) => left.name.localeCompare(right.name))
);

watch(
  currentAccount,
  account => {
    const settings = account?.settings?.internal_chat_settings || {};

    enabled.value = settings.enabled === true;
    inboxId.value = settings.inbox_id ? Number(settings.inbox_id) : null;
  },
  { immediate: true, deep: true }
);

const handleSubmit = async () => {
  if (enabled.value && !inboxId.value) {
    useAlert(t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.API.MISSING_INBOX'));
    return;
  }

  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        internal_chat_settings: {
          enabled: enabled.value,
          inbox_id:
            enabled.value && inboxId.value ? Number(inboxId.value) : null,
        },
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline outline-1 outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.NOTE') }}
      </p>
    </div>

    <div class="px-5 py-4">
      <form class="grid gap-4" @submit.prevent="handleSubmit">
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
        >
          <div class="flex items-start justify-between gap-4 p-4">
            <div class="space-y-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.ENABLED.TITLE') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{
                  t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.ENABLED.DESCRIPTION')
                }}
              </p>
            </div>
            <Switch v-model="enabled" />
          </div>

          <div class="grid gap-2 p-4">
            <label class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.INBOX.LABEL') }}
            </label>
            <select
              v-model="inboxId"
              class="!mb-0 text-sm"
              :disabled="!apiInboxes.length"
            >
              <option :value="null">
                {{ t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.INBOX.PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in apiInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                apiInboxes.length
                  ? t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.INBOX.HELP')
                  : t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.INBOX.EMPTY')
              }}
            </p>
          </div>
        </div>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.INTERNAL_CHAT.SAVE')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
